import { Vue, Component } from "vue-property-decorator";
import { IAppVersionFlag } from "@/types/appVersionFlag";
import hashStringToColor from "@/helpers/hashStringToColor";
import { IClientAppOption, TAppVersionFlagForm } from "../types";

import TableEmpty from "@/components/Includes/Table/TableFooter/TableEmpty/TableEmpty.vue";
import Loader from "@/components/Includes/Loader/Loader.vue";

@Component({
  methods: {
    hashStringToColor,
  },
  components: {
    Loader,
    TableEmpty,
    AppVersionFlagForm: () =>
      import("../AppVersionFlagForm/AppVersionFlagForm.vue"),
  },
})
export default class AppVersionFlagList extends Vue {
  private appVersionFlagForm: TAppVersionFlagForm | null = null;

  private isDeletingAppVersion = false;

  private appVersionFlags: {
    refreshKey: number;
    totalItems: number;
    page: number;
    limit: number;
    loading: boolean;
    list: IAppVersionFlag[];
  } = {
    refreshKey: Date.now(),
    totalItems: 0,
    limit: 30,
    page: 1,
    loading: false,
    list: [],
  };

  private clientApps: {
    loading: boolean;
    map: Map<number, IClientAppOption>;
  } = {
    loading: false,
    map: new Map(),
  };

  private get tableLoading() {
    return (
      this.clientApps.loading ||
      this.appVersionFlags.loading ||
      this.isDeletingAppVersion
    );
  }

  private get tableFields() {
    return [
      {
        key: "id",
        label: "ID",
      },
      {
        key: "createdAt",
        label: "Created at",
      },
      {
        key: "clientAppName",
        label: "App name",
      },
      {
        key: "appVersion",
        label: "App version",
      },
      {
        key: "di",
        label: "Allow DI",
      },
      {
        key: "actions",
        label: "",
      },
    ];
  }

  private get tableItems() {
    return this.appVersionFlags.list;
  }

  private get isShowedCreateModal() {
    return !!this.appVersionFlagForm && !this.appVersionFlagForm.id;
  }

  private set isShowedCreateModal(showed: boolean) {
    this.appVersionFlagForm = showed
      ? {
          id: 0,
          di: false,
          major: "",
          minor: "",
          patch: "",
          clientAppId: 0,
        }
      : null;
  }

  private get isShowedEditModal() {
    return !!this.appVersionFlagForm && !!this.appVersionFlagForm.id;
  }

  private set isShowedEditModal(showed: boolean) {
    if (showed) {
      return;
    }

    this.appVersionFlagForm = null;
  }

  private formatDate(val: string, showTime = true) {
    return new Date(val).toLocaleString("ru", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: showTime ? "2-digit" : undefined,
      minute: showTime ? "2-digit" : undefined,
    });
  }

  private toggleEditModal(isShowed: boolean) {
    this.isShowedEditModal = isShowed;
  }

  private toggleCreateModal(isShowed: boolean) {
    this.isShowedCreateModal = isShowed;
  }

  private seAppVersionFlagForm(form: TAppVersionFlagForm | null) {
    this.appVersionFlagForm = form;
  }

  private async deleteAppVersion(item: IAppVersionFlag) {
    this.isDeletingAppVersion = true;

    const clientApp = this.clientApps.map.get(item.id);
    const appName = clientApp ? clientApp.label : "";

    try {
      const { deleteAppVersionFlag } = await import(
        "@/api/appVersionFlag/deleteAppVersionFlag"
      );

      await deleteAppVersionFlag(item.id);
      this.$notify({
        group: "notice",
        title: "App version",
        text: `Version ${item.major}.${item.minor}.${item.patch} is deleted for ${appName}`,
        type: "success",
      });

      this.refreshAppVersionFlags();
    } catch {
      this.$notify({
        group: "notice",
        title: "App version",
        text: `Version ${item.major}.${item.minor}.${item.patch} is not deleted for ${appName}`,
        type: "error",
      });
    } finally {
      this.isDeletingAppVersion = false;
    }
  }

  private onSuccessUpdate() {
    this.appVersionFlags.page = 1;
    this.refreshAppVersionFlags();
    this.toggleEditModal(false);
  }

  private onSuccessCreate() {
    this.appVersionFlags.page = 1;
    this.refreshAppVersionFlags();
    this.toggleCreateModal(false);
  }

  private refreshAppVersionFlags() {
    this.appVersionFlags.refreshKey++;
  }

  private async fetchClientApps() {
    if (this.clientApps.loading) {
      return;
    }

    this.clientApps.loading = true;

    try {
      const { fetchClientApps } = await import(
        "@/api/clientApp/fetchClientApps"
      );

      const data = await fetchClientApps({ page: 1, limit: 512 });

      const clientAppsMap = new Map();

      data["hydra:member"].forEach(({ id, name }) => {
        clientAppsMap.set(id, {
          value: id,
          label: name,
        });
      });

      this.clientApps.map = clientAppsMap;
    } finally {
      this.clientApps.loading = false;
    }
  }

  private async fetchAppVersionFlags() {
    if (this.appVersionFlags.loading) {
      return;
    }

    this.appVersionFlags.loading = true;

    try {
      const { fetchAppVersionFlags } = await import(
        "@/api/appVersionFlag/fetchAppVersionFlags"
      );

      const data = await fetchAppVersionFlags({
        limit: this.appVersionFlags.limit,
        page: this.appVersionFlags.page,
      });

      this.appVersionFlags.list = data["hydra:member"].map((item) => {
        const clientApp = this.clientApps.map.get(item.clientAppId);

        return Object.freeze({
          ...item,
          di: Boolean(item.di),
          appVersion: `${item.major}.${item.minor}.${item.patch}`,
          clientAppName: clientApp && clientApp.label,
        });
      });

      this.appVersionFlags.totalItems = data["hydra:totalItems"];
    } finally {
      this.appVersionFlags.loading = false;
    }
  }

  private initAppVersionFlags() {
    this.$watch(
      () => {
        return [
          this.appVersionFlags.page,
          this.appVersionFlags.refreshKey,
        ].join("-");
      },
      this.fetchAppVersionFlags,
      {
        immediate: true,
      }
    );
  }

  private async mounted() {
    await this.fetchClientApps();
    this.initAppVersionFlags();
  }
}
